<script setup>
/**
 * How OKANE is used (docs/analytics-plan.md), for an admin: visitors and opens
 * per day over a chosen span, and every event counted with its labels. Only
 * shown when the session says `admin` — the server refuses everyone else
 * anyway. The days are the server's (UTC).
 */
import { computed, ref, watch } from 'vue'
import Chart from '../ui/Chart.vue'
import { addDays, shortDay, today } from '../../lib/dates'
import { summarise } from '../../lib/usage'
import { usageReport } from '../../stores/usage'
import { errorText, t } from '../../stores/i18n'

const SPANS = [7, 30, 90, 365]
const span = ref(30)
const report = ref(null)
const error = ref('')
const loading = ref(false)

const range = computed(() => ({ from: addDays(today(), -(span.value - 1)), to: today() }))

async function load() {
  loading.value = true
  error.value = ''
  const { from, to } = range.value
  try {
    report.value = { from, to, data: await usageReport(from, to) }
  } catch (err) {
    error.value = errorText(err)
  } finally {
    loading.value = false
  }
}
watch(range, load, { immediate: true })

const summary = computed(() => (report.value ? summarise(report.value.data, report.value.from, report.value.to) : null))
const series = computed(() =>
  summary.value
    ? [
        { label: t('usage.visitors'), values: summary.value.visitors, color: '--c-accent' },
        { label: t('usage.opens'), values: summary.value.opens, color: '--c-text', type: 'line' },
      ]
    : [],
)
const whole = (v) => String(Math.round(v))
/** Counts are whole: an axis step between two is left unlabelled. */
const tick = (v) => (Number.isInteger(v) ? String(v) : '')
</script>

<template>
  <div class="grid gap-4" data-usage-panel>
    <p class="text-[12.5px] text-faint">{{ t('usage.lead') }}</p>

    <div class="flex flex-wrap items-end gap-3">
      <div>
        <label class="label" for="usage-span">{{ t('usage.span') }}</label>
        <select id="usage-span" v-model.number="span" class="input w-auto">
          <option v-for="days in SPANS" :key="days" :value="days">{{ t('usage.lastDays', { count: days }) }}</option>
        </select>
      </div>
      <button class="btn-ghost" type="button" :disabled="loading" @click="load">{{ t('usage.refresh') }}</button>
    </div>

    <p v-if="error" class="text-[12.5px] text-danger" role="alert">{{ error }}</p>

    <template v-if="summary">
      <div class="grid grid-cols-3 gap-3" data-usage-totals>
        <div class="rounded-lg bg-raised px-3 py-2">
          <p class="text-[11.5px] text-faint">{{ t('usage.visitorDays') }}</p>
          <p class="tnum text-[18px] font-semibold">{{ summary.totals.visitors }}</p>
        </div>
        <div class="rounded-lg bg-raised px-3 py-2">
          <p class="text-[11.5px] text-faint">{{ t('usage.busiest') }}</p>
          <p class="tnum text-[18px] font-semibold">{{ summary.totals.busiest }}</p>
        </div>
        <div class="rounded-lg bg-raised px-3 py-2">
          <p class="text-[11.5px] text-faint">{{ t('usage.opens') }}</p>
          <p class="tnum text-[18px] font-semibold">{{ summary.totals.opens }}</p>
        </div>
      </div>

      <Chart
        type="bar"
        :labels="summary.days.map(shortDay)"
        :series="series"
        :format="whole"
        :tick-format="tick"
        :caption="t('usage.chartCaption')"
        :height="160"
      />

      <p v-if="!summary.events.length" class="text-[12.5px] text-faint">{{ t('usage.nothing') }}</p>
      <ul v-else class="grid gap-2" data-usage-events>
        <li v-for="e in summary.events" :key="e.name" class="rounded-lg border border-line px-3 py-2" :data-usage-event="e.name">
          <div class="flex items-baseline justify-between gap-3">
            <code class="text-[13px] font-medium">{{ e.name }}</code>
            <span class="tnum text-[13px] text-muted">{{ e.total }}</span>
          </div>
          <div v-if="e.labels.length" class="mt-1.5 flex flex-wrap gap-1.5">
            <span v-for="l in e.labels.slice(0, 8)" :key="l.label" class="rounded bg-raised px-1.5 py-0.5 text-[11.5px] text-muted">
              <code>{{ l.label }}</code> <span class="tnum">{{ l.n }}</span>
            </span>
            <span v-if="e.labels.length > 8" class="px-1 text-[11.5px] text-faint">{{ t('usage.more', { count: e.labels.length - 8 }) }}</span>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>
